import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Button,
  Flex,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DownloadOutlined,
  ExclamationCircleFilled,
  MinusCircleFilled,
  SyncOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';

import BulkStatusTag from './BulkStatusTag';
import QueryBoundary from 'components/QueryBoundary/QueryBoundary';

import useDownloadBulkOperationResults from './useDownloadBulkOperationResults';
import { BULK_UPLOAD_ROWS } from 'queries/bulkOperations';
import { formatDateTime } from 'utilities/datesAndTimes';
import {
  ROWS_PAGE_SIZE,
  ROW_STATUS,
  STEP_LABEL,
  TASK_OUTCOME,
  asOptions,
} from './constants';

/** Monospaced and unformatted so that `00000000123` is visibly not `123`. */
const AccountId = styled.span`
  font-family: var(--font-family-code, monospace);
  font-variant-numeric: tabular-nums;
`;

/**
 * Every row repeats every step, so an outcome is a glyph against plain text rather
 * than a tag: the colour left on the page is the colour of something to act on.
 */
const OUTCOME_GLYPH = Object.freeze({
  NOT_RUN: { Icon: MinusCircleFilled, color: 'var(--color-text-quaternary)' },
  IN_FLIGHT: { Icon: SyncOutlined, color: 'var(--color-info)' },
  COMPLETED: { Icon: CheckCircleFilled, color: 'var(--color-success)' },
  FAILED: { Icon: CloseCircleFilled, color: 'var(--color-error)' },
  ERROR: { Icon: ExclamationCircleFilled, color: 'var(--color-error)' },
  SKIPPED_DRY_RUN: {
    Icon: MinusCircleFilled,
    color: 'var(--color-text-quaternary)',
  },
});

const columns = [
  { title: 'Line', dataIndex: 'lineNumber', key: 'lineNumber', width: 80 },
  {
    title: 'Account',
    dataIndex: 'sourceId',
    key: 'sourceId',
    render: (sourceId) => <AccountId>{sourceId}</AccountId>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status) => <BulkStatusTag value={status} labels={ROW_STATUS} />,
  },
  {
    title: 'Steps',
    dataIndex: 'tasks',
    key: 'tasks',
    render: (tasks) =>
      tasks.length ? (
        <Flex vertical gap="var(--spacing-xxs)">
          {tasks.map(({ taskId, outcome, detail }) => {
            const { label } = TASK_OUTCOME[outcome] ?? { label: outcome };
            const { Icon, color } = OUTCOME_GLYPH[outcome] ?? {};

            return (
              <Tooltip key={taskId} title={detail || undefined}>
                <Typography.Text>
                  {Icon && (
                    <Icon
                      style={{ color, marginRight: 'var(--spacing-xxs)' }}
                    />
                  )}
                  {`${STEP_LABEL[taskId] ?? taskId}: ${label}`}
                </Typography.Text>
              </Tooltip>
            );
          })}
        </Flex>
      ) : (
        '—'
      ),
  },
  {
    title: 'Processed',
    dataIndex: 'processedTz',
    key: 'processedTz',
    render: (processedTz) =>
      processedTz ? formatDateTime(processedTz, true) : '—',
  },
];

const BulkOperationResults = ({ uploadToken, filename, idColumn }) => {
  const [status, setStatus] = useState();
  const [page, setPage] = useState(0);

  const rowsQuery = useQuery(BULK_UPLOAD_ROWS, {
    variables: { uploadToken, status, page, size: ROWS_PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });

  const [download, downloadState] = useDownloadBulkOperationResults({
    uploadToken,
    filename,
    status,
    idColumn,
  });

  const rows =
    rowsQuery.data?.bulkUploadRows ?? rowsQuery.previousData?.bulkUploadRows;

  /** For the reason `BulkUploadHistory` gives. */
  const boundary = rows
    ? { ...rowsQuery, data: rows, loading: false, dataState: 'complete' }
    : rowsQuery;

  const filterBy = (next) => {
    setStatus(next);
    setPage(0);
  };

  return (
    <>
      <Space style={{ marginBottom: 'var(--spacing-sm)' }} wrap>
        <Select
          allowClear
          placeholder="All results"
          aria-label="Filter results by status"
          style={{ minWidth: 180 }}
          value={status}
          onChange={filterBy}
          options={asOptions(ROW_STATUS)}
        />

        <Button
          icon={<DownloadOutlined />}
          onClick={download}
          loading={downloadState.loading}
          disabled={!rows?.totalElements}
        >
          Download CSV
        </Button>
      </Space>

      {downloadState.error && (
        <Alert
          showIcon
          type="error"
          title="Failed to download these account results"
          style={{ marginBottom: 'var(--spacing-sm)' }}
        />
      )}

      {/* The boundary wraps the table only: an agent who filtered to FAILED and
          hit a failing query would otherwise have nothing left to clear. */}
      <QueryBoundary
        query={boundary}
        handlePartialSuccess={false}
        loadingMessage="Loading results"
        errorMessage="Failed to load results"
      >
        <Table
          size="small"
          rowKey="lineNumber"
          loading={rowsQuery.loading}
          dataSource={rows?.content ?? []}
          columns={columns}
          locale={{ emptyText: 'No results match this filter.' }}
          pagination={{
            current: page + 1,
            pageSize: ROWS_PAGE_SIZE,
            total: rows?.totalElements ?? 0,
            showSizeChanger: false,
            onChange: (next) => setPage(next - 1),
          }}
        />
      </QueryBoundary>
    </>
  );
};

export default BulkOperationResults;
